import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { usePaymentProviders } from '../hooks/usePaymentProviders';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

interface LocationState {
    membershipTier?: string;
    firstName?: string;
    userResult?: {
        membershipUser?: Record<string, unknown>;
        [key: string]: unknown;
    };
    customClaims?: Record<string, unknown>;
    purchaseData?: {
        product?: any;
        priceId?: string;
        coupon?: any;
        giftCode?: any;
        invoicePreview?: any;
    };
}

const SuccessRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, isLoading: isAuthLoading, shouldRedirect } = useRequireAuth();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const locationState = location.state as LocationState | null;

    // Check if user came from signup via query parameter (persists on refresh)
    const fromSignup = searchParams.get('fromSignup') === 'true';

    // Query payment providers to check if user has stripeSubscription
    const { hasStripeSubscription, loading: isPaymentProvidersLoading, error: paymentProvidersError } = usePaymentProviders(user?.uid);

    // Show loading state while checking authentication and payment providers
    if (isAuthLoading || (user && isPaymentProvidersLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // REQUIRE AUTHENTICATION - users should be authenticated after signup
    if (shouldRedirect) {
        return <Navigate to="/" replace />;
    }

    // If there's an error loading payment providers, log it but don't block access
    // This allows the success page to show even if there's a temporary API issue
    if (paymentProvidersError) {
        console.error('Error loading payment providers:', paymentProvidersError);
    }

    // Allow access if user came from signup (via location.state OR query parameter)
    // Query parameter persists on refresh, location.state does not
    const cameFromSignup = !!(locationState || fromSignup);

    // If user is logged in with a subscription but didn't come from signup,
    // redirect to profile to prevent them from accessing success page directly
    if (hasStripeSubscription && !cameFromSignup) {
        return <Navigate to="/profile" replace />;
    }

    // Allow access if:
    // 1. User just completed signup (has location.state or fromSignup query param)
    // 2. User is authenticated but doesn't have subscription yet (edge case during signup)
    return <>{children}</>;
};

export default SuccessRoute;

